import { Empty, Rate, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { useGetReviewsQuery } from '@/modules/reviews/reviewsApi';
import type { ReviewStatus } from '@/types/review';
import { formatDateTime } from '@/utils/date';
import { ucfirst } from '@/utils/string';
import styles from './CustomerReviewsPanel.module.scss';

interface CustomerReviewsPanelProps {
  customerId: string;
}

function statusTag(status: ReviewStatus) {
  if (status === 'approved') return <Tag color="success">Approved</Tag>;
  if (status === 'rejected') return <Tag color="error">Rejected</Tag>;
  return <Tag color="gold">Pending</Tag>;
}

export function CustomerReviewsPanel({ customerId }: CustomerReviewsPanelProps) {
  const { data, isLoading } = useGetReviewsQuery({
    customerId,
    page: 1,
    limit: 50,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const reviews = data?.results ?? [];

  if (isLoading) {
    return <div className={styles.loading}>Loading reviews...</div>;
  }

  if (!reviews.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No reviews yet" />;
  }

  return (
    <div className={styles.list}>
      {reviews.map((review) => (
        <article key={review.id} className={styles.item}>
          <div className={styles.header}>
            <div className={styles.meta}>
              <Link to={`/products/${review.productId}/view`} className={styles.productLink}>
                {ucfirst(review.productName)}
              </Link>
              {review.productSku ? (
                <span className={styles.productSku}>{review.productSku}</span>
              ) : null}
              <Rate disabled allowHalf={false} value={review.rating} className={styles.rate} />
            </div>
            <div className={styles.badges}>
              {statusTag(review.status)}
              <span className={styles.date}>{formatDateTime(review.createdAt)}</span>
            </div>
          </div>

          <p className={styles.comment}>{review.comment}</p>
        </article>
      ))}
    </div>
  );
}
